import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { useInvoice } from '../context/InvoiceContext';
import { useConfirm } from '../context/ConfirmContext';
import { notificationService } from '../services/api';
import { formatTimeAgo, formatNotifMessage, getNotifLink, getNotifType, NOTIF_TYPES } from '../utils/notifications';

const STATUS_TABS = [
  { key: 'open', label: 'Open' },
  { key: 'done', label: 'Done' },
  { key: 'all',  label: 'All' },
];

const fmtStamp = (d) => new Date(d).toLocaleString('en-IN', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata'
});

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '4px 12px', borderRadius: '2px', fontSize: '12px', fontWeight: 600,
      letterSpacing: '0.02em', cursor: 'pointer', transition: 'all 0.15s',
      border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
      background: active ? 'rgba(183,28,28,0.06)' : 'transparent',
      color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
    }}
  >
    {children}
  </button>
);

const Activity = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { currentUser, showNotification } = useInvoice();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const typeOptions = NOTIF_TYPES.filter(t => isSuperAdmin || !t.adminOnly);

  const [status, setStatus] = useState(() => localStorage.getItem('activityStatus') || 'open');
  const [type, setType] = useState(() => localStorage.getItem('activityType') || '');
  const [items, setItems] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyIds, setBusyIds] = useState(() => new Set());

  const load = useCallback(async (before) => {
    const data = await notificationService.getAll({ status, type, before, limit: 50 });
    return data;
  }, [status, type]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .then(data => { if (!cancelled) { setItems(data.notifications); setNextBefore(data.nextBefore); } })
      .catch(err => showNotification(err.message || 'Failed to load activity', 'error'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load, showNotification]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await load(nextBefore);
      setItems(prev => {
        const seen = new Set(prev.map(n => n.id));
        return [...prev, ...data.notifications.filter(n => !seen.has(n.id))];
      });
      setNextBefore(data.nextBefore);
    } catch (err) {
      showNotification(err.message || 'Failed to load activity', 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const changeStatus = (v) => { setStatus(v); localStorage.setItem('activityStatus', v); };
  const changeType = (v) => { setType(v); localStorage.setItem('activityType', v); };

  const toggleDone = async (n) => {
    setBusyIds(s => new Set(s).add(n.id));
    try {
      if (n.isDone) await notificationService.markOpen([n.id]);
      else await notificationService.markDone({ ids: [n.id] });
      // Leave the filtered view once the item no longer matches it
      if (status === 'all') setItems(prev => prev.map(x => x.id === n.id ? { ...x, isDone: !n.isDone } : x));
      else setItems(prev => prev.filter(x => x.id !== n.id));
    } catch (err) {
      showNotification(err.message || 'Failed to update', 'error');
    } finally {
      setBusyIds(s => { const c = new Set(s); c.delete(n.id); return c; });
    }
  };

  const markAllDone = async () => {
    const typeLabel = typeOptions.find(t => t.value === type)?.label;
    const ok = await confirm(
      `Mark all open ${typeLabel ? typeLabel.toLowerCase() : 'activity'} as done?`,
      { title: 'Mark All Done', confirmLabel: 'Mark Done' }
    );
    if (!ok) return;
    try {
      const { count } = await notificationService.markDone({ all: true, type });
      if (status === 'all') setItems(prev => prev.map(x => (!type || getNotifType(x) === type) ? { ...x, isDone: true } : x));
      else { setItems([]); setNextBefore(null); }
      showNotification(`${count} marked as done`, 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to update', 'error');
    }
  };

  const typeLabelOf = (n) => NOTIF_TYPES.find(t => t.value === getNotifType(n))?.label;

  return (
    <>
      <div className="main-content">
        <div className="container">

          <div className="flex-between mb-lg" style={{ flexWrap: 'wrap', gap: 'var(--spacing-md)' }}>
            <h1>Activity</h1>
            {status !== 'done' && items.some(n => !n.isDone) && (
              <button className="btn btn-outline" onClick={markAllDone}>Mark all as done</button>
            )}
          </div>

          {/* Status tabs */}
          <div style={{ display: 'flex', gap: '2px', marginBottom: 'var(--spacing-md)', borderBottom: '2px solid var(--color-gray-200)' }}>
            {STATUS_TABS.map(t => (
              <button
                key={t.key}
                onClick={() => changeStatus(t.key)}
                style={{
                  padding: '6px 14px', background: 'none', border: 'none', cursor: 'pointer',
                  fontWeight: status === t.key ? 700 : 400,
                  color: status === t.key ? 'var(--color-primary)' : 'var(--color-secondary)',
                  borderBottom: status === t.key ? '2px solid var(--color-primary)' : '2px solid transparent',
                  marginBottom: '-2px', fontSize: '13px',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Type filter */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-lg)' }}>
            <Chip active={!type} onClick={() => changeType('')}>All</Chip>
            {typeOptions.map(t => (
              <Chip key={t.value} active={type === t.value} onClick={() => changeType(t.value)}>{t.label}</Chip>
            ))}
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {loading ? (
              <div className="flex-center" style={{ padding: 'var(--spacing-xl)' }}>
                <div className="spinner" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex-center" style={{ padding: 'var(--spacing-xl)', color: 'var(--color-text-secondary)' }}>
                {status === 'open' ? 'All caught up — nothing open.' : 'No activity found.'}
              </div>
            ) : (
              items.map(n => (
                <div
                  key={n.id}
                  onClick={() => navigate(getNotifLink(n))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)',
                    padding: '12px 16px', borderBottom: '1px solid var(--color-gray-100)',
                    cursor: 'pointer', opacity: n.isDone ? 0.6 : 1,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', lineHeight: 1.5,
                      textDecoration: n.isDone ? 'line-through' : 'none',
                    }}>
                      {formatNotifMessage(n)}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
                      {typeLabelOf(n) && <>{typeLabelOf(n)} · </>}
                      {formatTimeAgo(n.createdAt)} · {fmtStamp(n.createdAt)}
                    </div>
                  </div>
                  <button
                    className={n.isDone ? 'btn btn-secondary btn-sm' : 'btn btn-outline btn-sm'}
                    style={{ flexShrink: 0 }}
                    disabled={busyIds.has(n.id)}
                    onClick={e => { e.stopPropagation(); toggleDone(n); }}
                  >
                    {n.isDone ? 'Reopen' : '✓ Done'}
                  </button>
                </div>
              ))
            )}
          </div>

          {!loading && nextBefore && (
            <div className="flex-center" style={{ marginTop: 'var(--spacing-md)' }}>
              <button className="btn btn-secondary" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Activity;
